import { motion } from 'framer-motion';
import { 
  Shield, 
  Lock, 
  FileText, 
  Eye, 
  AlertCircle,
  CheckCircle2,
  Scale,
  UserCheck,
  Database,
  Phone,
  Bell,
  Heart,
  Users,
  Building2,
  Gavel
} from 'lucide-react';

export const HIPAA = () => {
  return (
    <div className="bg-zinc-950 min-h-screen pt-20">
      {/* Hero */}
      <section className="py-32 relative overflow-hidden" data-testid="hipaa-hero">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[150px]" />
        
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-emerald-500" />
              </div>
              <span className="overline">Privacy & Compliance</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8 font-['Outfit'] tracking-tighter">
              HIPAA Notice of<br />
              <span className="gradient-text">Privacy Practices</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl">
              This notice describes how medical information about you may be used and disclosed 
              and how you can get access to this information. Please review it carefully.
            </p>
            <p className="text-zinc-600 text-sm mt-6">
              Effective Date: January 1, 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-8 border-t border-white/5">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-white font-semibold mb-2">Important Notice</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  This Notice of Privacy Practices ("Notice") describes how HealthCheck Systems and its workforce members, 
                  including employees, volunteers, trainees, and other persons whose conduct is under our direct control, 
                  may use and disclose your Protected Health Information ("PHI") and your rights regarding your PHI. 
                  "Protected Health Information" means information that identifies you and relates to your past, present, 
                  or future physical or mental health condition, treatment, or payment for healthcare services.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Pledge */}
      <section className="py-16" data-testid="hipaa-pledge">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-surface rounded-2xl p-8 border-l-2 border-l-emerald-500"
          >
            <div className="flex items-center gap-4 mb-6">
              <Heart className="w-8 h-8 text-emerald-500" />
              <h2 className="text-2xl font-bold text-white font-['Outfit']">Our Pledge Regarding Your Health Information</h2>
            </div>
            <div className="text-zinc-400 leading-relaxed space-y-4">
              <p>
                We understand that your health information is personal and private. We are committed to protecting your 
                health information and will make reasonable efforts to ensure the confidentiality of your Protected Health Information.
              </p>
              <p>
                We are required by law to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Maintain the privacy of your Protected Health Information</li>
                <li>Provide you with this Notice of our legal duties and privacy practices with respect to your PHI</li>
                <li>Abide by the terms of the Notice currently in effect</li>
                <li>Notify you if we are unable to agree to a requested restriction on how your information is used or disclosed</li>
                <li>Accommodate reasonable requests you may make to communicate health information by alternative means or at alternative locations</li>
                <li>Notify you following a breach of your unsecured Protected Health Information</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How We May Use Your Information */}
      <section className="py-16 bg-zinc-900/30" data-testid="hipaa-uses">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">How We May Use and Disclose Your Health Information</h2>
            <p className="text-zinc-400">
              The following categories describe different ways that we may use and disclose your Protected Health Information 
              without your written authorization:
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                icon: Heart,
                title: 'For Treatment',
                content: `We may use your health information to provide you with medical treatment or services. We may disclose your health information to doctors, nurses, technicians, medical students, or other personnel who are involved in your care. For example, a doctor treating you for a broken leg may need to know if you have diabetes because diabetes may slow the healing process. Different departments of our organization may share health information about you in order to coordinate your care, such as prescriptions, lab work, and x-rays. We may also disclose your health information to people outside our organization who may be involved in your medical care, such as family members, other healthcare providers, or entities that provide equipment or services related to your treatment.`
              },
              {
                icon: Database,
                title: 'For Payment',
                content: `We may use and disclose your health information so that the treatment and services you receive may be billed and payment may be collected from you, an insurance company, or a third party. For example, we may need to give your health plan information about your screening services so your health plan will pay us or reimburse you for the services. We may also tell your health plan about a treatment you are going to receive to obtain prior approval or to determine whether your plan will cover the treatment. We may also disclose your health information to other healthcare providers for their payment activities.`
              },
              {
                icon: Building2,
                title: 'For Healthcare Operations',
                content: `We may use and disclose your health information for our healthcare operations. These uses and disclosures are necessary to run our organization and make sure that all of our patients receive quality care. For example, we may use health information to review our treatment and services and to evaluate the performance of our staff in caring for you. We may also combine health information about many patients to decide what additional services we should offer, what services are not needed, and whether certain new treatments are effective. We may also disclose information to doctors, nurses, technicians, medical students, and other personnel for review and learning purposes. We may also combine the health information we have with health information from other organizations to compare how we are doing and see where we can make improvements in the care and services we offer.`
              },
              {
                icon: Users,
                title: 'Business Associates',
                content: `We may disclose your health information to our business associates that perform functions on our behalf or provide us with services if the information is necessary for such functions or services. For example, we may use another company to perform billing services on our behalf. All of our business associates are obligated, under contract with us, to protect the privacy of your information and are not allowed to use or disclose any information other than as specified in our contract and as permitted by federal law.`
              },
              {
                icon: Bell,
                title: 'Appointment Reminders and Health-Related Benefits',
                content: `We may use and disclose your health information to contact you as a reminder that you have an appointment with us. We may also use and disclose your health information to tell you about health-related benefits or services, treatment alternatives, or other health-related products and services that may be of interest to you.`
              },
              {
                icon: Scale,
                title: 'As Required by Law',
                content: `We will disclose your health information when required to do so by federal, state, or local law. For example, we may disclose your health information when required by national security laws or public health disclosure laws.`
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="card-surface rounded-2xl p-8"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-emerald-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-white font-['Outfit']">{item.title}</h3>
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed">{item.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Special Situations */}
      <section className="py-16" data-testid="hipaa-special">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">Special Situations</h2>
            <p className="text-zinc-400">
              We may use or disclose your health information in the following situations without your authorization:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Public Health Activities', desc: 'To report diseases, injuries, vital events, and conduct public health surveillance, investigations, and interventions as required or permitted by law.' },
              { title: 'Health Oversight Activities', desc: 'To federal and state agencies that oversee our activities, such as the Department of Health and Human Services.' },
              { title: 'Lawsuits and Disputes', desc: 'In response to a court or administrative order or in response to a subpoena, discovery request, or other lawful process.' },
              { title: 'Law Enforcement', desc: 'For law enforcement purposes, such as in response to a court order, warrant, summons, or similar process.' },
              { title: 'Coroners and Funeral Directors', desc: 'To coroners, medical examiners, and funeral directors to carry out their duties.' },
              { title: 'Organ Donation', desc: 'To organizations that handle organ procurement or organ, eye, or tissue transplantation.' },
              { title: 'Research', desc: 'For research purposes under certain limited circumstances approved by an Institutional Review Board.' },
              { title: 'Serious Threat to Health or Safety', desc: 'To prevent a serious threat to your health and safety or the health and safety of the public or another person.' },
              { title: 'Military and Veterans', desc: 'If you are a member of the armed forces, we may release your health information as required by military command authorities.' },
              { title: 'Workers\' Compensation', desc: 'For workers\' compensation or similar programs that provide benefits for work-related injuries or illness.' },
              { title: 'Inmates', desc: 'If you are an inmate of a correctional institution or under the custody of a law enforcement official.' },
              { title: 'National Security', desc: 'To authorized federal officials for intelligence, counterintelligence, and other national security activities.' }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
                className="bg-zinc-900/50 rounded-xl p-6 border border-white/5"
              >
                <h4 className="text-white font-medium mb-2">{item.title}</h4>
                <p className="text-zinc-500 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Your Rights */}
      <section className="py-16 bg-zinc-900/30" data-testid="hipaa-rights">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">Your Rights Regarding Your Health Information</h2>
            <p className="text-zinc-400">
              You have the following rights regarding your health information that we maintain:
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                icon: Eye,
                title: 'Right to Inspect and Copy',
                content: `You have the right to inspect and copy your health information, such as medical and billing records, that we use to make decisions about your care. You must submit your request in writing. If you request a copy of the information, we may charge a fee for the costs of copying, mailing, or other supplies associated with your request. We may deny your request to inspect and copy in certain very limited circumstances. If you are denied access to your health information, you may request that the denial be reviewed.`
              },
              {
                icon: FileText,
                title: 'Right to Amend',
                content: `If you feel that health information we have about you is incorrect or incomplete, you may ask us to amend the information. You have the right to request an amendment for as long as the information is kept by us. Your request must be in writing and must provide a reason that supports your request. We may deny your request for an amendment if it is not in writing or does not include a reason to support the request. We may also deny your request if you ask us to amend information that: was not created by us; is not part of the health information kept by us; is not part of the information which you would be permitted to inspect and copy; or is accurate and complete.`
              },
              {
                icon: Database,
                title: 'Right to an Accounting of Disclosures',
                content: `You have the right to request an "accounting of disclosures." This is a list of the disclosures we made of your health information for purposes other than treatment, payment, healthcare operations, and certain other activities for the six years prior to your request. Your request must be in writing. The first list you request within a 12-month period will be free. For additional lists, we may charge you for the costs of providing the list. We will notify you of the cost involved and you may choose to withdraw or modify your request before any costs are incurred.`
              },
              {
                icon: Lock,
                title: 'Right to Request Restrictions',
                content: `You have the right to request a restriction or limitation on the health information we use or disclose about you for treatment, payment, or healthcare operations. You also have the right to request a limit on the health information we disclose about you to someone who is involved in your care or the payment for your care, such as a family member or friend. We are not required to agree to your request unless you are asking us to restrict the use and disclosure of your PHI to a health plan for payment or health care operation purposes and such information you wish to restrict pertains solely to a health care item or service for which you have paid us "out-of-pocket" in full. If we do agree, we will comply with your request unless the information is needed to provide you emergency treatment.`
              },
              {
                icon: Bell,
                title: 'Right to Request Confidential Communications',
                content: `You have the right to request that we communicate with you about medical matters in a certain way or at a certain location. For example, you can ask that we only contact you at work or by mail. Your request must be in writing and must specify how or where you wish to be contacted. We will accommodate all reasonable requests.`
              },
              {
                icon: FileText,
                title: 'Right to a Paper Copy of This Notice',
                content: `You have the right to a paper copy of this Notice. You may ask us to give you a copy of this Notice at any time. Even if you have agreed to receive this Notice electronically, you are still entitled to a paper copy.`
              },
              {
                icon: UserCheck,
                title: 'Right to Choose Someone to Act for You',
                content: `If you have given someone medical power of attorney or if someone is your legal guardian, that person can exercise your rights and make choices about your health information. We will make sure the person has this authority and can act for you before we take any action.`
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="card-surface rounded-2xl p-8"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-emerald-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-white font-['Outfit']">{item.title}</h3>
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed">{item.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Authorization */}
      <section className="py-16" data-testid="hipaa-authorization">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-surface rounded-2xl p-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Outfit']">Uses and Disclosures Requiring Your Written Authorization</h2>
            </div>
            <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
              <p>
                The following uses and disclosures of your Protected Health Information will be made only with your written authorization:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Most uses and disclosures of psychotherapy notes, where appropriate</li>
                <li>Uses and disclosures for marketing purposes</li>
                <li>Disclosures that constitute a sale of your Protected Health Information</li>
                <li>Other uses and disclosures not described in this Notice</li>
              </ul>
              <p>
                You may revoke an authorization at any time, in writing, except to the extent that we have already taken 
                action in reliance upon the authorization. Your revocation will not affect any use or disclosure permitted 
                by your authorization while it was in effect.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Changes to This Notice */}
      <section className="py-16 bg-zinc-900/30">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-surface rounded-2xl p-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Outfit']">Changes to This Notice</h2>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed">
              We reserve the right to change this Notice. We reserve the right to make the revised or changed Notice 
              effective for health information we already have about you as well as any information we receive in the future. 
              We will post a copy of the current Notice on our website. The Notice will contain on the first page, 
              in the top right-hand corner, the effective date. In addition, each time you register at or are admitted 
              to our facility for treatment or healthcare services, we will offer you a copy of the current Notice in effect.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Complaints */}
      <section className="py-16" data-testid="hipaa-complaints">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-surface rounded-2xl p-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Gavel className="w-5 h-5 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Outfit']">Complaints</h2>
            </div>
            <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
              <p>
                If you believe your privacy rights have been violated, you may file a complaint with us or with the 
                Secretary of the Department of Health and Human Services. To file a complaint with us, contact our 
                Privacy Officer at the address listed below. All complaints must be submitted in writing.
              </p>
              <p className="text-white font-medium">
                You will not be penalized or retaliated against for filing a complaint.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-zinc-900/30" data-testid="hipaa-contact">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-surface rounded-2xl p-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Phone className="w-5 h-5 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Outfit']">Contact Information</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-white font-medium mb-4">Privacy Officer</h3>
                <div className="text-zinc-400 text-sm space-y-1">
                  <p><strong className="text-white">HealthCheck Systems</strong></p>
                  <p>Attn: Privacy Officer</p>
                  <p>901 S Central Expy Ste 100</p>
                  <p>Richardson, TX 75080</p>
                  <p>Phone: (855) 831-2075</p>
                  <p>Email: privacy@healthchecksolutions.online</p>
                </div>
              </div>
              <div>
                <h3 className="text-white font-medium mb-4">Department of Health and Human Services</h3>
                <div className="text-zinc-400 text-sm space-y-1">
                  <p>Office for Civil Rights</p>
                  <p>U.S. Department of Health and Human Services</p>
                  <p>200 Independence Avenue, S.W.</p>
                  <p>Washington, D.C. 20201</p>
                  <p>Phone: 1-877-696-6775</p>
                  <p>Website: www.hhs.gov/ocr/privacy/hipaa/complaints/</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-8 border-t border-white/5">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <p className="text-zinc-600 text-sm">
            This Notice of Privacy Practices is effective as of January 1, 2024, and will remain in effect until replaced. 
            A copy of this Notice is available at our website at healthchecksolutions.online/hipaa.
          </p>
        </div>
      </section>
    </div>
  );
};
